import { useEffect } from "react";
import { useSelector, useDispatch } from "react-redux";
import { useLocation } from "react-router-dom";
import AuthHero from "../../components/auth/AuthHero";
import AuthMobileLogo from "../../components/auth/AuthMobileLogo";
import LanguageSelector from "../../components/common/LanguageSelector";
import VerificationStep from "../../components/auth/VerificationStep";
import PasswordStep from "../../components/auth/PasswordStep";
import {
  setInvitationContext,
  setSignupData,
} from "../../store/slices/signupSlice";

const SignUp = () => {
  const dispatch = useDispatch();
  const location = useLocation();
  const { verificationStep } = useSelector((state) => state.signup);

  // Parse invitation deep-link parameters
  useEffect(() => {
    const searchParams = new URLSearchParams(location.search || "");
    const stateInvite = location.state?.mandateInvite || {};

    const token =
      searchParams.get("token") ||
      searchParams.get("invite_token") ||
      stateInvite.token ||
      null;
    const type = searchParams.get("type") || stateInvite.type || null;
    const emailParam = searchParams.get("email") || stateInvite.email || null;
    const roleTypeParam = searchParams.get("role_type") || null;
    const firstNameParam = searchParams.get("first_name") || null;
    const lastNameParam = searchParams.get("last_name") || null;
    const inviteSentByParam =
      searchParams.get("invite_sent_by") || stateInvite.inviteSentBy || null;
    const wyscoutId =
      searchParams.get("wyscout_id") || stateInvite.wyscoutId || null;
    const senderType =
      searchParams.get("sender_type") || stateInvite.senderType || null;
    const mobileParam =
      searchParams.get("mobile") || stateInvite.mobile || null;

    // Get specific IDs based on sender type instead of using invite_sent_by
    const agentId = searchParams.get("agent_id") || stateInvite.agentId || null;
    const agencyId =
      searchParams.get("agency_id") || stateInvite.agencyId || null;
    const clubId = searchParams.get("club_id") || stateInvite.clubId || null;

    // Fix email + character issue: URLSearchParams converts + to space
    // We need to get the raw parameter and decode it properly
    let email = emailParam;
    if (emailParam) {
      // Check if this looks like a malformed email with space that should be +
      if (emailParam.includes(" ") && emailParam.includes("@")) {
        // Try to fix common case: "user space number@domain" -> "user+number@domain"
        const beforeAt = emailParam.split("@")[0];
        const afterAt = emailParam.split("@")[1];
        if (beforeAt && afterAt && beforeAt.includes(" ")) {
          // Replace spaces with + in the local part (before @)
          const fixedBeforeAt = beforeAt.replace(/\s+/g, "+");
          email = `${fixedBeforeAt}@${afterAt}`;
        }
      }
    }

    // Normalize mobile so we can store it in signup state
    const sanitizedMobile = mobileParam?.replace(/\s+/g, "") || null;
    const normalizedPhoneNumber = sanitizedMobile
      ? sanitizedMobile.replace(/^\+/, "")
      : null;

    console.log("SignUp - Parsing invitation parameters:", {
      token,
      type,
      email,
      originalEmail: emailParam,
      wyscoutId,
      senderType,
      agentId,
      agencyId,
      clubId,
      roleTypeParam,
      firstNameParam,
      lastNameParam,
      inviteSentByParam,
      mobile: sanitizedMobile,
    });

    // If this is an invitation flow (has token and type)
    if (token && type) {
      // Determine the correct ID to use based on sender type and invitation type
      let finalAgentId = null;
      let finalAgencyId = null;
      let finalClubId = null;

      if (senderType === "agent" && type === "player") {
        // Player invited by agent mandate - use agent_id
        finalAgentId = agentId ? parseInt(agentId, 10) : null;
      } else if (senderType === "agency" && type === "agent") {
        // Agent invited by agency admin - use agency_id
        finalAgencyId = agencyId ? parseInt(agencyId, 10) : null;
      } else if (
        senderType === "club" &&
        (type === "player" || type === "staff" || type === "coach")
      ) {
        // Player, staff or coach invited by club - use club_id
        finalClubId = clubId ? parseInt(clubId, 10) : null;
      }

      dispatch(
        setInvitationContext({
          token,
          type,
          email: email, // Use the fixed email
          wyscoutId: wyscoutId ? parseInt(wyscoutId, 10) : null,
          agentId: finalAgentId,
          agencyId: finalAgencyId,
          clubId: finalClubId,
          senderType,
          roleType: roleTypeParam || null,
          firstName: firstNameParam || null,
          lastName: lastNameParam || null,
          inviteSentBy: inviteSentByParam ? parseInt(inviteSentByParam, 10) : null,
          mobile: sanitizedMobile,
          isInvitationFlow: true,
        })
      );

      // Pre-fill email if provided - use the fixed email
      if (email) {
        console.log("SignUp - Pre-filling email:", email);
        dispatch(setSignupData({ email }));
      }

      if (normalizedPhoneNumber) {
        dispatch(setSignupData({ phoneNumber: normalizedPhoneNumber }));
      }
    } else if (normalizedPhoneNumber) {
      // Even without token/type, persist phone if supplied via navigation state
      dispatch(setSignupData({ phoneNumber: normalizedPhoneNumber }));
    }
  }, [location.search, location.state, dispatch]);

  return (
    <div className="flex min-h-screen flex-col">
      <div className="flex flex-1">
        <AuthHero pageName={"signup"} />
        {/* max-md:pt-28 reserves the band the centred mobile wordmark occupies
            (absolute, top-16 + h-12 = 112px) so the vertically centred form
            can never slide up underneath it on short viewports; max-md:pb-24
            keeps the form clear of the fixed support FAB bottom-right. */}
        <div className="flex-1 bg-white flex items-center justify-center relative max-md:pt-28 max-md:pb-24">
          {/* Mobile-only wordmark — the desktop logo sits in AuthHero's dark
              panel, which is hidden below md. */}
          <AuthMobileLogo />
          {/* Language Selector - Top Right */}
          <div className="absolute top-4 right-4 z-10">
            <LanguageSelector isFromAuth={true}/>
          </div>
          <div className="w-full max-w-xl px-5">
            {verificationStep === "password" ? (
              <PasswordStep />
            ) : (
              <VerificationStep />
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default SignUp;
